import React, { FC } from 'react'
import { TextField } from '../atoms'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import { ProfilePictureForm } from './ProfilePictureForm'
import { IoOpenOutline } from 'react-icons/io5'
import { getEnvVars } from '../../lib/config'
import { makeStyles } from 'tss-react/mui'
import { customBorderRadius } from '../../lib/styles/universal'
import { isUserAdminOrOwner } from '../../lib/utils'
import { RPUser } from '@hazadapt-git/public-core-base'
import { Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import { PermissionsList } from './PermissionsList'
import { ProfileFormMode } from '../../lib/entities'

interface ProfileFormProps {
    activeUser?: RPUser // currently logged in user, primarily for permissions
    targetUser: RPUser // user being edited/updated/added
    onSubmit: (values: Partial<RPUser>) => void
    mode: ProfileFormMode
    profilePictureFormRef?: React.RefObject<HTMLDivElement>
    formikRef: React.Ref<FormikProps<Partial<RPUser>>>
    FormikStateBridge: React.FC
}

const { authUrl } = getEnvVars()

export const ProfileDetailsForm: FC<ProfileFormProps> = ({
    formikRef,
    FormikStateBridge,
    activeUser,
    targetUser,
    onSubmit,
    mode,
    profilePictureFormRef,
}: ProfileFormProps) => {
    const { classes: localClasses } = useLocalStyles()

    const initialValues = {
        first_name: targetUser?.first_name ?? '',
        last_name: targetUser?.last_name ?? '',
        title: targetUser?.title ?? '',
        email: targetUser?.email ?? '',
        profile_picture_uri: targetUser?.profile_picture_uri ?? '',
        permissions: targetUser?.permissions ?? [],
    }

    // anywhere a user is being edited, self or other
    const userDetailModes = [
        ProfileFormMode.SETUP_SELF,
        ProfileFormMode.MANAGE_SELF,
        ProfileFormMode.EDIT_MEMBER,
        ProfileFormMode.ADD_MEMBER,
    ]
    // only in setup and manage profile
    const profileModes = [
        ProfileFormMode.SETUP_SELF,
        ProfileFormMode.MANAGE_SELF,
    ]
    // where permissions are being set or edited
    const permissionModes = [
        ProfileFormMode.PERMISSIONS,
        ProfileFormMode.ADD_MEMBER,
    ]

    const validationSchema = Yup.object().shape({
        first_name: Yup.string().required('First name is required'),
        last_name: Yup.string().required('Last name is required'),
        // 'title' is not rendered in all modes and as such needs to be conditionally required
        title: permissionModes.includes(mode)
            ? Yup.string()
            : Yup.string().required('Title is required'),
        email: Yup.string()
            .strict(true)
            .matches(
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                'Invalid email address'
            )
            .required('Email is required'),
    })

    const showFirstName = userDetailModes.includes(mode)
    const showLastName = userDetailModes.includes(mode)
    const showTitle =
        userDetailModes.includes(mode) && mode !== ProfileFormMode.ADD_MEMBER
    const showEmail = userDetailModes.includes(mode)
    const disableEmail =
        mode !== ProfileFormMode.ADD_MEMBER &&
        !isUserAdminOrOwner(activeUser ?? targetUser)

    const showHazAdaptId = profileModes.includes(mode)
    const showPassword = profileModes.includes(mode)
    const showProfilePictureForm = profileModes.includes(mode)
    const showPermissions = permissionModes.includes(mode) && activeUser

    const getHelperText = (touched?: boolean, error?: string) => {
        if (touched && error) return error
        if (error) return 'Required'
        return ''
    }

    return (
        <div className={localClasses.container}>
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={onSubmit}
                validateOnMount
            >
                {({
                    values,
                    handleChange,
                    handleBlur,
                    touched,
                    errors,
                    setFieldValue,
                }) => (
                    <Form>
                        <FormikStateBridge />
                        <Grid container spacing={4}>
                            {showFirstName && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="first_name"
                                        label="First Name"
                                        placeholder="John"
                                        value={values.first_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.first_name &&
                                                errors.first_name
                                        )}
                                        helperText={getHelperText(
                                            touched.first_name,
                                            errors.first_name
                                        )}
                                        inputProps={{
                                            'aria-label': 'First Name',
                                        }}
                                    />
                                </Grid>
                            )}
                            {showLastName && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="last_name"
                                        label="Last Name"
                                        placeholder="Doe"
                                        value={values.last_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.last_name &&
                                                errors.last_name
                                        )}
                                        helperText={getHelperText(
                                            touched.last_name,
                                            errors.last_name
                                        )}
                                        inputProps={{
                                            'aria-label': 'Last Name',
                                        }}
                                    />
                                </Grid>
                            )}
                            {showTitle && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="title"
                                        label="Title"
                                        placeholder="Emergency Management Coordinator"
                                        value={values.title}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.title && errors.title
                                        )}
                                        helperText={getHelperText(
                                            touched.title,
                                            errors.title
                                        )}
                                        inputProps={{ 'aria-label': 'Title' }}
                                    />
                                </Grid>
                            )}
                            {showEmail && (
                                <Grid
                                    item
                                    xs={12}
                                    md={mode === 'add-member' ? 12 : 6}
                                >
                                    <TextField
                                        name="email"
                                        label="Email Address"
                                        placeholder="johndoe@example.com"
                                        value={values.email}
                                        type="email"
                                        disabled={disableEmail}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.email && errors.email
                                        )}
                                        helperText={getHelperText(
                                            touched.email,
                                            errors.email
                                        )}
                                        subtext={
                                            disableEmail
                                                ? "To change the email address on your ResiliencePoint profile, ask your organization's ResiliencePoint administrator."
                                                : null
                                        }
                                        inputProps={{
                                            'aria-label': 'Email Address',
                                        }}
                                    />
                                </Grid>
                            )}
                            {showHazAdaptId && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="HazAdapt ID"
                                        value={targetUser?.hazadapt_id_email}
                                        type="email"
                                        disabled
                                        inputProps={{
                                            'aria-label': 'HazAdapt ID',
                                        }}
                                        subtext={
                                            <span>
                                                Go to your{' '}
                                                <Link
                                                    href={authUrl}
                                                    target="_blank"
                                                >
                                                    HazAdapt ID settings{' '}
                                                    <IoOpenOutline />
                                                </Link>{' '}
                                                to change your HazAdapt ID
                                                email.
                                            </span>
                                        }
                                    />
                                </Grid>
                            )}
                            {showPassword && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Password"
                                        value="****************"
                                        type="password"
                                        disabled
                                        inputProps={{
                                            'aria-label': 'Password',
                                        }}
                                        subtext={
                                            <span>
                                                Go to your{' '}
                                                <Link
                                                    href={authUrl}
                                                    target="_blank"
                                                >
                                                    HazAdapt ID settings{' '}
                                                    <IoOpenOutline />
                                                </Link>{' '}
                                                to change your password.
                                            </span>
                                        }
                                    />
                                </Grid>
                            )}

                            {showProfilePictureForm && (
                                <Grid item xs={12}>
                                    <div
                                        ref={profilePictureFormRef}
                                        className={
                                            localClasses.profilePictureFormWrapper
                                        }
                                    >
                                        <ProfilePictureForm
                                            profilePicture={
                                                values.profile_picture_uri ??
                                                undefined
                                            }
                                            onChangeProfilePicture={(img) =>
                                                setFieldValue(
                                                    'profile_picture_uri',
                                                    img
                                                )
                                            }
                                        />
                                    </div>
                                </Grid>
                            )}
                            {showPermissions && (
                                <Grid item xs={12}>
                                    <PermissionsList
                                        activeUser={activeUser}
                                        selectedPermissions={
                                            values.permissions ?? []
                                        }
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Form>
                )}
            </Formik>
        </div>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        margin: '0 auto',
        width: '100%',
        maxWidth: '70rem',
    },
    profilePictureFormWrapper: {
        transition: 'all 0.5s ease-in-out',
        borderRadius: customBorderRadius,
        padding: '1rem',
    },
})
